import {
  composeClassNames,
  ifDefined,
  universalEach,
  withDefault,
} from "@faedev/editor-utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { CSSProperties, ReactNode } from "react";
import { Button, CardDeck } from "react-bootstrap";
import Heading from "src/components/Heading";
import constants from "src/constants";
import getLink from "src/functions/getLink";
import gutter from "src/functions/gutter";
import translate from "src/functions/translate";
import validateContent from "src/functions/validateContent";
import Small_chevron_down from "src/icons/Small-chevron-down";
import Small_chevron_right from "src/icons/Small-chevron-right";
import { sButton } from "src/styles/sButton.module.scss";
import { sGroupTitle } from "src/styles/sGroupTitle.module.scss";
import { sVideoList } from "src/styles/sVideoList.module.scss";
import { ApiPlaylistCard } from "src/types/ApiPlaylistCard";
import { VariantSize } from "src/types/VariantSize";
import { VariantVideoPreview } from "src/types/VariantVideoPreview";
import PlaylistCard from "./PlaylistCard";

type Props = {
  size: VariantSize;
  title?: ReactNode | null | undefined;
  data: ApiPlaylistCard[];
  type: VariantVideoPreview;
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  limit?: number | null | undefined;
  showMore?: (boolean | string) | null | undefined;
  showPrivate?: boolean | null | undefined;
};

function PlaylistList({
  size,
  title,
  data,
  type,
  style,
  className,
  limit,
  showMore,
  showPrivate,
}: Props) {
  const { locale: lang } = useRouter();

  return data.length > 0 ? (
    <div
      style={style}
      className={composeClassNames([
        sVideoList,
        className,
        "video-list-wrapper",
      ])}
    >
      {title ? (
        <Heading
          type="h2"
          title={title}
          className={composeClassNames([sGroupTitle, "video-list-title"])}
        />
      ) : null}
      <CardDeck className={composeClassNames([size, "video-list"])}>
        {(() => {
          let validated = false;
          let count = 0;
          let actualLimit = withDefault(
            limit,
            showMore ? constants.List.limitVideos : constants.List.limitAll
          );
          let sortData = data.sort((a, b) =>
            a.order > b.order ? 1 : b.order > a.order ? -1 : 0
          );

          return universalEach(sortData, (item, i) => {
            validated = validateContent(
              withDefault(item.blocked, false),
              ifDefined(
                item.verified,
                (value) => value.verified,
                () => false
              ),
              showPrivate ? false : withDefault(item.private, false)
            );

            validated && count++;
            return count <= actualLimit && validated ? (
              <>
                <PlaylistCard
                  data={item}
                  type={withDefault(type, `preview`)}
                  size={size}
                  bunch={data.length}
                  serialNo={count}
                  key={`video-card-` + String(count)}
                />
              </>
            ) : null;
          });
        })()}
      </CardDeck>
      {constants.Show.contentMore &&
      typeof showMore === `boolean` &&
      showMore ? (
        data.length > withDefault(limit, constants.List.limitVideos) ? (
          <div className={"show-more"} style={gutter(`margin`, `bottom`, `lg`)}>
            <Button
              as="a"
              variant="outline-primary"
              className={composeClassNames([sButton, "btn-icon-before"])}
            >
              {/* TODO: this version is not finalized, there should be onClick and action view rest of videos behind limit */}
              <Small_chevron_down />
              {withDefault(showMore, translate(`Show more`, lang))}
            </Button>
          </div>
        ) : null
      ) : null}
      {constants.Show.contentMore &&
      typeof showMore === `string` &&
      showMore.length > 0 ? (
        data.length > withDefault(limit, constants.List.limitVideos) ? (
          <div className={"show-more"} style={gutter(`margin`, `bottom`, `lg`)}>
            <Link href={getLink(lang, `classes`, null)} passHref>
              <Button
                as="a"
                variant="outline-primary"
                className={composeClassNames([sButton, "btn-icon-before"])}
              >
                <Small_chevron_right />
                {withDefault(
                  `${showMore} (+${data.length - limit})`,
                  translate(`Show more`, lang)
                )}
              </Button>
            </Link>
          </div>
        ) : null
      ) : null}
    </div>
  ) : null;
}

export default PlaylistList;
